<template>
  <page-container>
    <div class="flex justify-between items-center">
      <heading size="gigantic">
        <span v-if="isEditMode && currentTeam">{{ currentTeam.name }}</span>
        <span v-else>{{ $t("nav.addTeam") }}</span>
      </heading>

      <div>
        <b-button
          v-if="!isTeamOwner && isEditMode"
          @click="leaveTeam"
          type="is-danger"
        >
          <nice-icon icon="sign-out" spacing-right></nice-icon>
          <span>Leave team</span>
        </b-button>
      </div>
    </div>

    <fieldset
      v-if="isTeamOwner || !isEditMode"
      :disabled="$wait.is('creating team') || $wait.is('loading team')"
    >
      <b-field label="Name" class="mb4">
        <b-input
          v-model="team.name"
          required
          ref="nameInput"
          :loading="$wait.is('getting team title')"
        ></b-input>
      </b-field>

      <div class="mt3 mb5 flex justify-between">
        <div>
          <b-button
            type="is-primary"
            @click="create()"
            :disabled="isSaveButtonDisabled"
            :loading="$wait.is('creating team')"
            >{{ $t("buttons.save") }}</b-button
          >
          <b-button
            @click="cancel()"
            :disabled="$wait.is('creating team')"
            class="ml3"
            >{{ $t("buttons.cancel") }}</b-button
          >
        </div>
        <div>
          <b-button v-if="currentTeam" type="is-danger" @click="deleteTeam()">
            {{ $t("buttons.deleteTeam") }}
          </b-button>
        </div>
      </div>
    </fieldset>

    <fieldset v-if="isEditMode" :disabled="!currentTeam">
      <h3 class="f4 b">{{ $t("nav.teamMembers") }}</h3>

      <div
        v-if="currentTeam && currentTeam.members && currentTeam.members.length"
      >
        <team-member
          v-for="(member, memberIndex) in currentTeam.members"
          :key="memberIndex"
          :member="member"
        />
      </div>
      <p v-else class="is-muted mt3">Team has no members.</p>

      <div v-if="isTeamOwner || !isEditMode">
        <h3 class="f5 b mt4 mb1">Add member</h3>
        <p class="mb2">
          Currently you can only invite users who already have a tekk account.
        </p>
        <b-field class="mb4">
          <b-input
            v-model="newMember.user.identity"
            :loading="$wait.is('getting team title')"
            @keyup.native.enter.exact="addNewMember"
            placeholder="Email of the user"
            expanded
          ></b-input>
          <p class="control">
            <button
              class="button is-primary"
              style="height:100%;"
              @click="addNewMember"
              :disabled="newMember.user.identity.length < 3"
            >
              <!-- <nice-icon icon="check"></nice-icon> -->
              {{ $t("buttons.invite") }}
            </button>
          </p>
        </b-field>
      </div>
    </fieldset>
  </page-container>
</template>

<script>
import TagList from "@/shared/components/tag/List";
import TagItem from "@/shared/components/tag/Item.vue";
import TeamMember from "@/components/team/Member.vue";

function initalState() {
  return {
    team: {
      name: "",
      members: []
    },
    newMember: {
      user: {
        identity: ""
      }
    },
    isActivated: false
  };
}

export default {
  data() {
    return {
      team: initalState().team,
      newMember: initalState().newMember,
      isActivated: initalState().isActivated
    };
  },

  watch: {
    $initCompleted: {
      immediate: true,
      async handler(val) {
        if (val) {
          if (this.isEditMode) {
            this.$nextTick(() => {
              const team =
                this.$store.getters["team/teamsArray"] &&
                this.$store.getters["team/teamsArray"].find(
                  t => t.id == this.$route.params.id
                );
              this.$store.commit("team/SET_CURRENT_ID", team.id);
              this.$set(
                this,
                "team",
                this.$utils.cloneDeep(this.$store.getters["team/current"])
              );
            });
          } else {
            this.reset();
          }
        }
      }
    }
  },

  methods: {
    addNewMember() {
      this.$store.dispatch("team/addMember", {
        member: this.$utils.cloneDeep(this.newMember)
      });
      this.$set(this, "newMember", initalState().newMember);
    },

    async create() {
      if (this.team.name.length < 1) {
        this.$buefy.toast.open({
          message: `Name can not be empty!`,
          type: "is-danger"
        });
        return false;
      }

      let team = true;

      if (!this.isEditMode) {
        team = await this.$store.dispatch("team/createTeam", {
          team: this.team
        });
      } else {
        team = await this.$store.dispatch("team/updateTeam", {
          team: this.team
        });
      }
      this.$set(this, "team", this.$utils.cloneDeep(team));

      this.$router.push({ name: "team.page", params: { id: team.id } });
    },

    reset() {
      this.$set(this, "team", initalState().team);
      this.$set(this, "newMember", initalState().newMember);
    },

    cancel() {
      this.$router.back();
    },

    deleteTeam() {
      this.$buefy.dialog.confirm({
        title: "Delete team",
        message: `Are you sure you want to delete <span class="i">"${this.currentTeam.name}</span>? This can not be undone!`,
        hasIcon: true,
        type: "is-danger",
        animation: "pop-modal",
        onConfirm: async () => {
          await this.$store.dispatch("team/deleteTeam", { team: this.team });
          this.$router.replace("/resources");
        }
      });
    },

    leaveTeam() {
      this.$buefy.dialog.confirm({
        title: "Leave team",
        message: `Are you sure you want to leave <span class="i">${this.currentTeam.name}</span>? None of your posted resources will be deleted.`,
        hasIcon: true,
        type: "is-danger",
        animation: "pop-modal",
        onConfirm: async () => {
          await this.$store.dispatch("team/leaveTeam", { member: this.member });
          this.$router.replace("/resources");
        }
      });
    }
  },

  computed: {
    teamId() {
      return (
        this.$store.getters["team/teamsArray"] &&
        this.$store.getters["team/teamsArray"].find(
          t => t.id == this.$route.params.id
        )
      );
    },
    isEditMode() {
      return this.$route.path.includes("/edit");
    },

    isSaveButtonDisabled() {
      return !this.team.name;
    }
  },

  created() {
    this.isActivated = true;
  },
  beforeDestroy() {
    this.isActivated = false;
  },

  components: {
    TagList,
    TagItem,
    TeamMember
  }
};
</script>

<style lang="scss" scoped></style>
